import { useSyncExternalStore } from "react";
import { EnumMcpUserModelEnforcementMode, type McpUserModel } from "@/api";
import type {
  EnforcementEnvironment,
  EnforcementMethod,
} from "@/api/temp/AgentEstateService";
import { isAccessDenied } from "@/helpers/isAccessDenied";
import { registerAgentSessionReset } from "@/libs/agentSession";

/** Only the saved tenant strategy is authoritative. The API has no health probe. */
export const environmentFromMcpUser = (
  user: McpUserModel | null | undefined,
): EnforcementEnvironment => {
  const mode = user?.enforcementMode;
  if (!mode) {
    const configured = Boolean(user?.credentials);
    return {
      status: configured ? "CONFIGURATION_REQUIRED" : "NOT_ENABLED",
      configured: false,
      enabled: false,
      method: "NONE",
      healthStatus: "UNKNOWN",
    };
  }

  const methods: Record<EnumMcpUserModelEnforcementMode, EnforcementMethod> = {
    [EnumMcpUserModelEnforcementMode.PROXY]: "MCP_GATEWAY",
    [EnumMcpUserModelEnforcementMode.INTERCEPTOR]: "PLUGIN",
    [EnumMcpUserModelEnforcementMode.MCP]: "OTHER",
    [EnumMcpUserModelEnforcementMode.MIDDLEWARE]: "OTHER",
  };
  const method = methods[mode];
  if (!method) return unknownTenantEnforcement();
  return {
    status: "ENABLED",
    configured: true,
    enabled: true,
    method,
    healthStatus: "UNKNOWN",
  };
};

/**
 * The saved record could not be read. `checkError` keeps why, so a caller that
 * lacks access is told so rather than being shown a generic outage - and
 * neither is ever reported as "no gateway configured".
 */
export const unknownTenantEnforcement = (
  checkError?: EnforcementEnvironment["checkError"],
): EnforcementEnvironment => ({
  status: "UNKNOWN",
  configured: false,
  enabled: false,
  method: "NONE",
  healthStatus: "UNKNOWN",
  ...(checkError ? { checkError } : {}),
});

/** A failed read's `checkError`: access, or any other outage. */
export const checkErrorOf = (
  error: unknown,
): NonNullable<EnforcementEnvironment["checkError"]> =>
  isAccessDenied(error) ? "FORBIDDEN" : "UNAVAILABLE";

let current = unknownTenantEnforcement();
let revision = 0;
/** Whether the saved record has been read this session, even if unchanged. */
let known = false;
const listeners = new Set<() => void>();

const commit = (environment: EnforcementEnvironment) => {
  current = environment;
  revision += 1;
  listeners.forEach(listener => listener());
};

/**
 * The tenant's saved enforcement, for non-React readers (the mock layer).
 * Components read it through `useTenantEnforcement`, which re-renders them
 * when it changes.
 */
export const tenantEnforcementSnapshot = () => current;
export const tenantEnforcementRevision = () => revision;
export const isTenantEnforcementKnown = () => known;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The tenant's saved enforcement, kept current as the bridge commits it. */
export const useTenantEnforcement = (): EnforcementEnvironment =>
  useSyncExternalStore(subscribe, tenantEnforcementSnapshot);

/**
 * The preview keeps only status/method, never the backend credentials.
 * Unchanged values keep the previous object, so subscribers see a stable
 * snapshot and only re-render on a real change.
 */
export const setTenantEnforcement = (environment: EnforcementEnvironment) => {
  known = true;
  const changed = JSON.stringify(current) !== JSON.stringify(environment);
  if (changed) commit(environment);
  return changed;
};

registerAgentSessionReset("tenant-enforcement", () => {
  known = false;
  commit(unknownTenantEnforcement());
});
